import React, { useState } from 'react';
import { useGetSprintsQuery } from '../store/sprintApi';
import { Link } from 'react-router-dom';
import DataTable from './DataTable';
import Pagination from './Pagination';

const ITEMS_PER_PAGE = 10;

const statusBadge = (status: string) => {
  const s = (status || '').toLowerCase();
  if (s === 'published') return <span className="badge-green">✓ Published</span>;
  if (s === 'started') return <span className="badge-yellow">⏳ In Progress</span>;
  if (s === 'unpublished') return <span className="badge-gray">Unpublished</span>;
  return <span className="badge-gray">{status || '—'}</span>;
};

const SprintList: React.FC = () => {
  const { data: sprints = [], error, isLoading } = useGetSprintsQuery();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const filtered = sprints.filter(s =>
    (s.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  if (error) {
    return (
      <div className="text-center py-10 text-red-500">
        <p className="font-semibold">Error loading sprints.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display font-bold text-xl text-trident-gray-900">Available Sprints</h2>
          <p className="text-sm text-trident-gray-500">Total: {sprints.length} sprints</p>
        </div>
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-trident-gray-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            type="text"
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            placeholder="Search sprints..."
            className="input-field pl-10 sm:w-64"
          />
        </div>
      </div>

      <DataTable
        loading={isLoading}
        rows={paginated}
        rowKey={(s) => s._id}
        emptyMessage={searchTerm ? `No sprints match "${searchTerm}"` : 'No sprints yet'}
        columns={[
          {
            key: 'sno', label: '#', width: '60px', align: 'center',
            render: (_r, i) => <span className="text-trident-gray-400 font-mono text-xs">{(currentPage - 1) * ITEMS_PER_PAGE + i + 1}</span>,
          },
          {
            key: 'name', label: 'Sprint Name',
            render: (s) => <div className="font-semibold text-trident-gray-900">{s.name}</div>,
          },
          {
            key: 'date', label: 'Created',
            render: (s) => <span className="text-sm text-trident-gray-600">{new Date(s.date).toLocaleDateString()}</span>,
          },
          { key: 'status', label: 'Status', render: (s) => statusBadge(s.status) },
          {
            key: 'actions', label: 'Actions', align: 'right',
            render: (s) => (
              <Link
                to={`/admin/sprints/${s._id}`}
                className="inline-flex items-center gap-1.5 text-sm font-display font-semibold text-trident-orange hover:text-trident-orange-dark transition-colors"
              >
                View Forms
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </Link>
            ),
          },
        ]}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={filtered.length}
        itemsPerPage={ITEMS_PER_PAGE}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default SprintList;
